/**
 * Utility functions for extracting and substituting template variables.
 * 
 * Variables use the format {{variableName}} (double curly braces).
 * These are replaced with values from contact data during calls.
 */

import { normalizeAgentDynamicVariableDefinition, type AgentDynamicVariableValue } from './agentDynamicVariables';

const PROTECTED_VARIABLE_PREFIXES = ['sys__', 'system__'];

export function isProtectedSystemVariableKey(key: string): boolean {
  const normalized = key.trim().toLowerCase();
  return PROTECTED_VARIABLE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * Extracts variable names from a text using {{variableName}} pattern.
 * Returns unique variable names sorted alphabetically.
 * 
 * @example
 * extractVariables("Hello {{name}}, welcome to {{company}}!")
 * // Returns: ["company", "name"]
 */
export function extractVariables(text: string): string[] {
  if (!text) return [];
  
  const regex = /\{\{(\w+)\}\}/g;
  const variables = new Set<string>();
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    variables.add(match[1]);
  }
  
  return Array.from(variables).sort();
}

/**
 * Extracts variables from multiple text sources.
 * Returns unique variable names sorted alphabetically.
 * 
 * @example
 * extractVariablesFromMultiple(["Hello {{name}}", "From {{company}}"])
 * // Returns: ["company", "name"]
 */
export function extractVariablesFromMultiple(texts: (string | undefined)[]): string[] {
  const allVariables = new Set<string>();
  
  for (const text of texts) {
    if (text) {
      const variables = extractVariables(text);
      variables.forEach(v => allVariables.add(v));
    }
  }
  
  return Array.from(allVariables).sort();
}

/**
 * Substitutes variables in text with provided values.
 * Variables without values remain as {{variableName}}.
 * 
 * @example
 * substituteVariables("Hello {{name}}!", { name: "John" })
 * // Returns: "Hello John!"
 */
export function substituteVariables(
  text: string,
  values: Record<string, string>
): string {
  if (!text) return text;
  
  let result = text;
  for (const [key, value] of Object.entries(values)) {
    if (value) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }
  }
  return result;
}

/**
 * Returns list of variables that are missing values.
 * A variable is considered missing if its value is undefined, null, or empty string.
 * 
 * @example
 * getMissingVariables(["name", "company"], { name: "John" })
 * // Returns: ["company"]
 */
export function getMissingVariables(
  variables: string[],
  values: Record<string, AgentDynamicVariableValue> | undefined
): string[] {
  if (!values) return variables;
  return variables.filter((variable) => {
    const rawValue = values[variable];
    if (rawValue === undefined) {
      return true;
    }
    return normalizeAgentDynamicVariableDefinition(rawValue).value.trim() === '';
  });
}

/**
 * Checks if all detected variables have dynamic values.
 * 
 * @example
 * hasAllDynamicVariables("Hello {{name}}", { name: "John" })
 * // Returns: true
 */
export function hasAllDynamicVariables(
  text: string,
  values: Record<string, AgentDynamicVariableValue> | undefined
): boolean {
  const variables = extractVariables(text);
  if (variables.length === 0) return true;
  return getMissingVariables(variables, values).length === 0;
}

/**
 * Validates dynamic variables and returns validation result.
 */
export interface VariableValidationResult {
  isValid: boolean;
  detectedVariables: string[];
  missingVariables: string[];
  message?: string;
}

export function validateDynamicVariables(
  systemPrompt: string | undefined,
  firstMessage: string | undefined,
  dynamicVariables: Record<string, AgentDynamicVariableValue> | undefined
): VariableValidationResult {
  const detectedVariables = extractVariablesFromMultiple([systemPrompt, firstMessage])
    .filter((variable) => !isProtectedSystemVariableKey(variable));
  
  if (detectedVariables.length === 0) {
    return {
      isValid: true,
      detectedVariables: [],
      missingVariables: [],
    };
  }
  
  const missingVariables = getMissingVariables(detectedVariables, dynamicVariables);
  
  return {
    isValid: missingVariables.length === 0,
    detectedVariables,
    missingVariables,
    message: missingVariables.length > 0
      ? `Missing dynamic variable values: ${missingVariables.join(', ')}`
      : undefined,
  };
}
